import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { FcpFloatLabelComponent, FloatLabelVariant } from '../fcp-float-label';

@Component({
    selector: 'fcp-input-number',
    standalone: true,
    imports: [CommonModule, FormsModule, InputNumberModule, FcpFloatLabelComponent],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FcpInputNumberComponent
        }
    ],
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <div class="fcp-input-number-container">
            <ng-template #inputTemplate>
                <p-inputNumber
                    [ngModel]="modelValue()"
                    [inputId]="id()"
                    [mode]="mode()"
                    [currency]="currency()"
                    [currencyDisplay]="currencyDisplay()"
                    [locale]="locale()"
                    [localeMatcher]="localeMatcher()"
                    [prefix]="prefix()"
                    [suffix]="suffix()"
                    [min]="min()"
                    [max]="max()"
                    [step]="stepValue()"
                    [minFractionDigits]="effectiveMinFractionDigits()"
                    [maxFractionDigits]="effectiveMaxFractionDigits()"
                    [useGrouping]="useGrouping()"
                    [placeholder]="showFloatLabel() ? '' : placeholder()"
                    [disabled]="isDisabled()"
                    [showButtons]="showButtons()"
                    [buttonLayout]="buttonLayout()"
                    [size]="size()"
                    [fluid]="fluid()"
                    [invalid]="invalid()"
                    [class.ng-invalid]="invalid()"
                    [class.ng-dirty]="invalid()"
                    [class.fcp-input-number-disabled]="isDisabled()"
                    (onInput)="handleInput($event.value)"
                    (onBlur)="handleBlur()"
                />
            </ng-template>

            @if (showFloatLabel()) {
                <fcp-float-label [variant]="inputNumberFloatLabelVariant" [invalid]="invalid()">
                    <ng-container *ngTemplateOutlet="inputTemplate" />
                    <label [for]="id()">{{ placeholder() }}</label>
                </fcp-float-label>
            } @else {
                <ng-container *ngTemplateOutlet="inputTemplate" />
            }
        </div>
    `
})
export class FcpInputNumberComponent implements ControlValueAccessor {
    readonly inputNumberFloatLabelVariant = FloatLabelVariant.In;

    e2e = input<string | null>(null);
    id = input<string | undefined>(undefined);
    placeholder = input<string>('');
    disabled = input<boolean>(false);
    floatLabel = input<boolean>(false);
    prefix = input<string | undefined>();
    suffix = input<string | undefined>();
    min = input<number | undefined>();
    max = input<number | undefined>();
    step = input<number | undefined>(1);
    integerOnly = input<boolean>(false);
    minFractionDigits = input<number | undefined>();
    maxFractionDigits = input<number | undefined>();
    useGrouping = input<boolean>(true);
    mode = input<FcpInputNumberMode>(FcpInputNumberMode.Decimal);
    currency = input<string | undefined>();
    currencyDisplay = input<FcpInputNumberCurrencyDisplay | undefined>();
    locale = input<string | undefined>();
    localeMatcher = input<FcpInputNumberLocaleMatcher | undefined>();
    showButtons = input<boolean>(false);
    buttonLayout = input<FcpInputNumberButtonLayout>(FcpInputNumberButtonLayout.Stacked);
    size = input<FcpInputNumberSize | undefined>(undefined);
    fluid = input<boolean>(false);
    invalid = input<boolean>(false);

    onChange = output<number | null>();

    private _value = signal<number | null>(null);
    private _onChange = signal<(value: number | null) => void>(() => {});
    private _onTouched = signal<() => void>(() => {});
    private _disabledFromForms = signal<boolean>(false);

    modelValue = computed(() => this._value());
    isDisabled = computed(() => this.disabled() || this._disabledFromForms());
    stepValue = computed(() => this.step() ?? 1);
    effectiveMinFractionDigits = computed(() => (this.integerOnly() ? 0 : this.minFractionDigits()));
    effectiveMaxFractionDigits = computed(() => (this.integerOnly() ? 0 : this.maxFractionDigits()));
    protected showFloatLabel = computed(() => this.floatLabel());

    writeValue(value: number | null): void {
        this._value.set(value);
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this._onChange.set(fn);
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched.set(fn);
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabledFromForms.set(isDisabled);
    }

    handleInput(value: number | string | null | undefined): void {
        // PrimeNG may emit `value` as `string` (typed in input) or `null`,
        // and in some keyboard-driven paths it can result in `NaN`/empty string.
        const nextValue = this.parseValue(value);

        this._value.set(nextValue);
        this._onChange()(nextValue);
        this.onChange.emit(nextValue);
    }

    handleBlur(): void {
        this._onTouched()();
    }

    private parseValue(value: number | string | null | undefined): number | null {
        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '') {
                return null;
            }

            const asNumber = Number(trimmed);
            return Number.isNaN(asNumber) ? null : asNumber;
        }

        // value is a number
        return Number.isNaN(value) ? null : value;
    }
}

export enum FcpInputNumberSize {
    Small = 'small'
}

export enum FcpInputNumberMode {
    Decimal = 'decimal',
    Currency = 'currency'
}

export enum FcpInputNumberButtonLayout {
    Stacked = 'stacked',
    Horizontal = 'horizontal',
    Vertical = 'vertical'
}

export enum FcpInputNumberCurrencyDisplay {
    Symbol = 'symbol',
    Code = 'code',
    Name = 'name'
}

export enum FcpInputNumberLocaleMatcher {
    Lookup = 'lookup',
    BestFit = 'best fit'
}
